/**
 * Recruiter-facing submission / pipeline code vs row PK.
 *
 * - Display (UI text): `job_seeker_applied_jobs.submission_id`
 *   → payload keys: `submission_display_id`, `submission_code`, or (submission-agent
 *     result rows only) `submission_id` when it is the display code.
 * - ATS deep link: encrypt(`job_seeker_applied_jobs.id`) → `submission_encrypted_id`
 * - API / complete-choice: row PK as `submission_id` / `job_seeker_applied_job_id`
 */

function asIdString(value: unknown): string {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(Math.trunc(value));
  }
  return '';
}

/**
 * Explicit display-code fields only — never treat Voice AI / interview
 * `lookup_rows.submission_id` (row PK) as a display value.
 */
export function resolveSubmissionDisplayIdFromExplicitFields(
  row: Record<string, unknown> | null | undefined,
): string {
  if (!row) {
    return '';
  }
  return (
    asIdString(row['submission_display_id']) ||
    asIdString(row['submissionDisplayId']) ||
    asIdString(row['submission_code']) ||
    asIdString(row['submissionCode']) ||
    ''
  );
}

/**
 * For submission / pipeline **result** rows.
 * Prefer explicit display fields; never show the row PK as the badge.
 */
export function resolveSubmissionDisplayIdForResultRow(
  row: Record<string, unknown> | null | undefined,
): string {
  if (!row) {
    return '';
  }
  const explicit = resolveSubmissionDisplayIdFromExplicitFields(row);
  if (explicit) {
    return explicit;
  }
  const sid = asIdString(row['submission_id']) || asIdString(row['submissionId']);
  const applied =
    asIdString(row['applied_job_id']) || asIdString(row['appliedJobId']);
  // When Domain marks applied_job_id and it equals submission_id, that value is the PK.
  if (applied && sid && applied === sid) {
    return '';
  }
  return sid;
}

/** Format `#123` for UI badges / labels. */
export function formatSubmissionDisplayBadge(displayId: string | null | undefined): string | null {
  const sid = (displayId ?? '').trim();
  return sid ? `#${sid}` : null;
}
